import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import {
  TABLE_DEFAULTS_BY_CURRENCY,
  mineschestMultipliersSchema,
  resolveCurrencyTable,
  type IntegratorGameConfig,
  type MinesChestMultipliersOverride,
} from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime, fmtNumber } from '../format.js';
import { showToast } from '../toast.js';

export interface MinesChestMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/**
 * Baked defaults and bounds. They mirror `DEFAULT_RTP_BPS`, `DEFAULT_MAX_PICKS`
 * and the limits beside them in packages/games/mineschest/src/chest-math.ts,
 * which are also the bounds on `mineschestMultipliersSchema` in
 * @apx/integrator-config. Duplicated rather than imported to keep the game
 * module out of the admin bundle; `mineschest.test.ts` checks each one, and the
 * payout maths below, against the game's own file. If the game changes one,
 * change it here in the same commit.
 */
export const MINESCHEST_DEFAULT_RTP_BPS = 9_600;
export const MINESCHEST_MIN_RTP_BPS = 5_000;
export const MINESCHEST_MAX_RTP_BPS = 10_000;
export const MINESCHEST_RTP_STEP_BPS = 50;
export const MINESCHEST_DEFAULT_MAX_PICKS = 9;
export const MINESCHEST_MIN_PICKS = 1;
export const MINESCHEST_PICKS_CEILING = 10;

/** Twenty chests, ten of them explosive every round. */
const CHEST_COUNT = 20;
const SAFE_COUNT = 10;

/** `C(n, k)`, exact. */
function binomial(n: number, k: number): bigint {
  let r = 1n;
  for (let i = 1; i <= k; i++) r = (r * BigInt(n - k + i)) / BigInt(i);
  return r;
}

function isTicketSize(n: number): boolean {
  return Number.isInteger(n) && n >= MINESCHEST_MIN_PICKS && n <= MINESCHEST_PICKS_CEILING;
}

/** Chance that a bet on `n` chests wins: `C(10, n) / C(20, n)`. */
export function mineschestWinProbability(n: number): number {
  if (!isTicketSize(n)) return 0;
  return Number(binomial(SAFE_COUNT, n)) / Number(binomial(CHEST_COUNT, n));
}

/**
 * What a winning bet on `n` chests returns per unit stake, in HUNDREDTHS
 * (912 = 9.12x), stake included: `floor(k × C(20, n) / (100 × C(10, n)))`.
 * The RTP over the chance of winning, cut to two decimals and never rounded up.
 *
 * Kept in hundredths, not bps. Ten chests pay 17,736,576 hundredths at 96%,
 * which is 1,773,657,600 bps: still a safe integer, but close enough to the
 * 32-bit limit that nothing here stores, adds or bit-shifts a payout in bps.
 */
export function mineschestPayoutHundredths(n: number, rtpBps: number): number {
  if (!isTicketSize(n) || !Number.isInteger(rtpBps) || rtpBps <= 0) return 0;
  return Number((BigInt(rtpBps) * binomial(CHEST_COUNT, n)) / (100n * binomial(SAFE_COUNT, n)));
}

/** `9.12x`, `16,124.16x`. Integer arithmetic, two decimals, always. */
export function mineschestFormatMultiplier(hundredths: number): string {
  const h = Math.max(0, Math.trunc(hundredths));
  const whole = Math.trunc(h / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${whole}.${(h % 100).toString().padStart(2, '0')}x`;
}

/** A whole number of bps from 50.00% to 100.00%. The step is the schema's to
 *  enforce on a save; this is the rule the game reads a stored value by. */
function isStoredRtpBps(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MINESCHEST_MIN_RTP_BPS &&
    value <= MINESCHEST_MAX_RTP_BPS
  );
}

function isStoredMaxPicks(value: unknown): value is number {
  return typeof value === 'number' && isTicketSize(value);
}

export type MinesChestSettingKey = 'rtpBps' | 'maxPicks';
export type MinesChestSettingSource = 'override' | 'inherited';

export interface MinesChestSetting {
  /** The value in force at this scope. */
  value: number;
  /** What this scope falls back to when it sets nothing itself. */
  inherited: number;
  source: MinesChestSettingSource;
}

/**
 * Each setting through its layers: this scope's own stored value, then the
 * provider's, then the baked default. The two settings are resolved apart,
 * because a stored surface may carry one without the other.
 *
 * A stored value that is not a legal setting is read as absent, which is how
 * the game reads it: it falls back rather than failing.
 */
export function mineschestResolveSettings(
  own: unknown,
  provider: unknown,
): Record<MinesChestSettingKey, MinesChestSetting> {
  const o = (own ?? {}) as { rtpBps?: unknown; maxPicks?: unknown };
  const p = (provider ?? {}) as { rtpBps?: unknown; maxPicks?: unknown };
  const inheritedRtp = isStoredRtpBps(p.rtpBps) ? p.rtpBps : MINESCHEST_DEFAULT_RTP_BPS;
  const inheritedPicks = isStoredMaxPicks(p.maxPicks) ? p.maxPicks : MINESCHEST_DEFAULT_MAX_PICKS;
  return {
    rtpBps: isStoredRtpBps(o.rtpBps)
      ? { value: o.rtpBps, inherited: inheritedRtp, source: 'override' }
      : { value: inheritedRtp, inherited: inheritedRtp, source: 'inherited' },
    maxPicks: isStoredMaxPicks(o.maxPicks)
      ? { value: o.maxPicks, inherited: inheritedPicks, source: 'override' }
      : { value: inheritedPicks, inherited: inheritedPicks, source: 'inherited' },
  };
}

/** bps to the two-decimal percent the form shows: 9600 reads "96.00". */
export function mineschestBpsToPct(bps: number): string {
  return (bps / 100).toFixed(2);
}

/**
 * A typed percentage to bps: "96.5" is 9650. An empty or unreadable field
 * becomes 0, which the schema refuses.
 *
 * A figure with more than two decimals is NOT rounded to the nearest bps. It
 * stays a fraction, so the schema refuses it and the form never saves a
 * setting nobody typed.
 */
export function mineschestPctToBps(pctInput: string): number {
  const text = pctInput.trim();
  const pct = Number(text);
  if (text === '' || !Number.isFinite(pct)) return 0;
  return Number((pct * 100).toFixed(4));
}

/** The typed number of chests, or 0 (refused) for an empty or unreadable field. */
export function mineschestParsePicks(picksInput: string): number {
  const text = picksInput.trim();
  const picks = Number(text);
  return text === '' || !Number.isFinite(picks) ? 0 : picks;
}

/** What the form's two inputs show for a pair of resolved settings. */
export function mineschestFormValues(settings: Record<MinesChestSettingKey, MinesChestSetting>): {
  rtpPct: string;
  maxPicks: string;
} {
  return {
    rtpPct: mineschestBpsToPct(settings.rtpBps.value),
    maxPicks: String(settings.maxPicks.value),
  };
}

/**
 * The `multipliers` surface the form proposes: ALWAYS both settings.
 *
 * An approved proposal replaces this scope's stored `multipliers` whole; it is
 * not merged into what was there. A proposal carrying one setting would drop
 * the other from the stored surface, and the game would then run it at a
 * fallback nobody chose. So the setting the admin did not touch is sent too,
 * at the value the form loaded for it.
 */
export function mineschestProposal(
  rtpPctInput: string,
  maxPicksInput: string,
): MinesChestMultipliersOverride {
  return {
    version: 1,
    rtpBps: mineschestPctToBps(rtpPctInput),
    maxPicks: mineschestParsePicks(maxPicksInput),
  };
}

export const MINESCHEST_RTP_RULE = `RTP must be ${mineschestBpsToPct(MINESCHEST_MIN_RTP_BPS)} to ${mineschestBpsToPct(MINESCHEST_MAX_RTP_BPS)}%, in steps of ${mineschestBpsToPct(MINESCHEST_RTP_STEP_BPS)}.`;
export const MINESCHEST_PICKS_RULE = `Most chests per bet must be a whole number from ${MINESCHEST_MIN_PICKS} to ${MINESCHEST_PICKS_CEILING}.`;

/**
 * Why a proposal cannot be saved, one line per setting, or an empty list when
 * it can. Each value is judged by the game's own schema, so the form accepts
 * exactly the values the API accepts; only the wording is this page's.
 *
 * It is stricter than the schema in one way: the schema takes either setting
 * on its own, and this page never sends one alone (see `mineschestProposal`),
 * so a proposal missing a setting is refused here.
 */
export function mineschestProposalErrors(proposal: unknown): string[] {
  const out: string[] = [];
  const add = (line: string): void => {
    if (!out.includes(line)) out.push(line);
  };
  const given = (proposal ?? {}) as { rtpBps?: unknown; maxPicks?: unknown };
  if (given.rtpBps === undefined) add(MINESCHEST_RTP_RULE);
  if (given.maxPicks === undefined) add(MINESCHEST_PICKS_RULE);
  const parsed = mineschestMultipliersSchema.safeParse(proposal);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = issue.path[0];
      // A proposal with neither setting fails the schema as a whole; the two
      // lines above already say what is missing.
      if (key === undefined && out.length > 0) continue;
      add(
        key === 'rtpBps'
          ? MINESCHEST_RTP_RULE
          : key === 'maxPicks'
            ? MINESCHEST_PICKS_RULE
            : issue.message,
      );
    }
  }
  // Form order, whichever check found the problem first.
  return out.sort((a, b) => rank(a) - rank(b));
}

function rank(line: string): number {
  return line === MINESCHEST_RTP_RULE ? 0 : line === MINESCHEST_PICKS_RULE ? 1 : 2;
}

export interface MinesChestPayoutRow {
  /** How many chests the bet holds, 1 – 10. */
  chests: number;
  /** Chance that it wins, 0 – 1. */
  chance: number;
  /** What a win returns per unit stake, in hundredths, stake included. */
  payoutHundredths: number;
  /** The exact return of this bet, 0 – 1. Never more than the RTP setting. */
  rtp: number;
  /** false above `maxPicks`: the table does not take this bet. */
  offered: boolean;
}

/** All ten bets under a pair of settings, offered or not. */
export function mineschestPayoutRows(rtpBps: number, maxPicks: number): MinesChestPayoutRow[] {
  const rows: MinesChestPayoutRow[] = [];
  for (let n = MINESCHEST_MIN_PICKS; n <= MINESCHEST_PICKS_CEILING; n++) {
    const chance = mineschestWinProbability(n);
    const payoutHundredths = mineschestPayoutHundredths(n, rtpBps);
    rows.push({
      chests: n,
      chance,
      payoutHundredths,
      rtp: (payoutHundredths / 100) * chance,
      offered: n <= maxPicks,
    });
  }
  return rows;
}

/**
 * The most one bet can return: the table's maximum bet on the largest bet the
 * table offers, rounded down like any payout. A BigInt, because a large
 * maximum bet times the ten-chest payout passes what a number holds exactly.
 */
export function mineschestLargestWin(maxBet: number, maxPicks: number, rtpBps: number): bigint {
  if (!Number.isFinite(maxBet) || maxBet <= 0) return 0n;
  return (BigInt(Math.trunc(maxBet)) * BigInt(mineschestPayoutHundredths(maxPicks, rtpBps))) / 100n;
}

export interface MinesChestMaxBet {
  currency: string;
  maxBet: number;
}

type TableLayer = Pick<IntegratorGameConfig, 'table' | 'tableByCurrency'> | null | undefined;

/**
 * The maximum bet in force for each currency this game has a table in: this
 * scope's own table over the provider's over the baked row, resolved the way
 * the Table limits tab resolves it. IDR first, then by code.
 */
export function mineschestMaxBets(
  gameId: string,
  own: TableLayer,
  provider: TableLayer,
): MinesChestMaxBet[] {
  const ownByCurrency = own?.tableByCurrency ?? {};
  const providerByCurrency = provider?.tableByCurrency ?? {};
  const merged = {
    table: own?.table ?? provider?.table,
    tableByCurrency: { ...providerByCurrency, ...ownByCurrency },
  } as IntegratorGameConfig;
  const currencies = new Set<string>([
    ...Object.keys(TABLE_DEFAULTS_BY_CURRENCY[gameId] ?? {}),
    ...Object.keys(providerByCurrency),
    ...Object.keys(ownByCurrency),
  ]);
  return [...currencies]
    .sort((a, b) => (a === 'IDR' ? -1 : b === 'IDR' ? 1 : a.localeCompare(b)))
    .map((currency) => ({
      currency,
      maxBet: resolveCurrencyTable(merged, gameId, currency).maxBet,
    }));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function chestsWord(n: number): string {
  return n === 1 ? '1 chest' : `${n} chests`;
}

/**
 * The RTP summary lists one row per NUMBER of chests, keyed on one ticket of
 * that size (chests 1 to n), because every ticket of a size returns the same.
 * Read back as the size it stands for: `MINESCHEST_1_2_3` is "3 chests". Null
 * for anything that is not one of this game's bet types.
 */
export function mineschestSizeLabel(betType: string): string | null {
  const m = /^MINESCHEST_([1-9][0-9]?(?:_[1-9][0-9]?){0,9})$/.exec(betType);
  return m ? chestsWord(m[1]!.split('_').length) : null;
}

function rtpColor(rtp: number): string {
  if (rtp >= 1.0) return 'var(--error)';
  if (rtp > 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/**
 * The ten-row table of what a pair of settings produces. A bet above
 * `maxPicks` keeps its row, dimmed and tagged, so whoever moves the setting
 * sees what raising it would open.
 */
export function mineschestPayoutTableHtml(rtpBps: number, maxPicks: number): string {
  const rows = mineschestPayoutRows(rtpBps, maxPicks);
  const offered = rows.filter((r) => r.offered).map((r) => r.rtp);
  const body = rows
    .map(
      (r) => `<tr data-chests="${r.chests}" data-offered="${r.offered ? 'yes' : 'no'}"${
        r.offered ? '' : ' style="opacity:0.45"'
      }>
        <td><strong>${chestsWord(r.chests)}</strong></td>
        <td style="white-space:nowrap">${(r.chance * 100).toFixed(6)}%</td>
        <td style="white-space:nowrap"><strong>${mineschestFormatMultiplier(r.payoutHundredths)}</strong></td>
        <td style="white-space:nowrap;color:${rtpColor(r.rtp)}">${(r.rtp * 100).toFixed(4)}%</td>
        <td>${r.offered ? '<span class="muted">Offered</span>' : '<span class="pill">Not offered</span>'}</td>
      </tr>`,
    )
    .join('');
  const range =
    offered.length > 0
      ? `Bets on offer return ${(Math.min(...offered) * 100).toFixed(2)}% to ${(Math.max(...offered) * 100).toFixed(2)}%. ` +
        `Payouts are cut to two decimals and never rounded up, so no bet returns more than the RTP setting.`
      : '';
  return `<table>
      <thead><tr><th>Chests in the bet</th><th>Chance to win</th><th>Pays</th><th>RTP of this bet</th><th></th></tr></thead>
      <tbody>${body}</tbody>
    </table>
    <p class="muted" style="margin-top:10px">${range}</p>`;
}

/**
 * The largest possible win beside the settings that set it: the payout for
 * `maxPicks` chests times the table's maximum bet, per currency. With no table
 * to read, the multiplier alone, said in words.
 */
export function mineschestLargestWinHtml(
  rtpBps: number,
  maxPicks: number,
  maxBets: readonly MinesChestMaxBet[] | null,
): string {
  const pays = mineschestFormatMultiplier(mineschestPayoutHundredths(maxPicks, rtpBps));
  const lead = `<p style="margin:0 0 8px">A winning bet on <strong>${chestsWord(maxPicks)}</strong>, the largest bet these settings offer, pays <strong>${pays}</strong>. There is no cap on a win.</p>`;
  if (!maxBets || maxBets.length === 0) {
    return `${lead}<p class="muted" style="margin:0">Largest possible win: <strong>${pays}</strong> × the table's maximum bet.</p>`;
  }
  const body = maxBets
    .map(
      (m) => `<tr data-currency="${escapeHtml(m.currency)}">
        <td><strong>${escapeHtml(m.currency)}</strong></td>
        <td style="white-space:nowrap">${fmtNumber(m.maxBet)}</td>
        <td style="white-space:nowrap"><strong>${fmtNumber(mineschestLargestWin(m.maxBet, maxPicks, rtpBps))}</strong></td>
      </tr>`,
    )
    .join('');
  return `${lead}<table>
      <thead><tr><th>Currency</th><th>Maximum bet</th><th>Largest possible win</th></tr></thead>
      <tbody>${body}</tbody>
    </table>
    <p class="muted" style="margin-top:10px">Maximum bet × ${pays}, for one bet. The maximum bet is set on the Table limits tab. A player may hold more than one bet in a round, and players who pick the same chests win together.</p>`;
}

/**
 * Mines Chest settings editor.
 *
 * The game has no paytable to type: a bet on `n` chests returns the RTP
 * constant over its chance of winning, cut to two decimals. So the surface
 * carries two settings, `rtpBps` and `maxPicks`, and this page shows the ten
 * payouts and the largest possible win they produce as they are typed.
 *
 * Every proposal carries BOTH settings, in either scope (see
 * `mineschestProposal`). In integrator scope each setting also shows whether
 * its stored value is this integrator's own or inherited from the provider
 * default, so whoever saves can see that the save turns both into the
 * integrator's own. Changes ride the two-admin approval flow.
 *
 * A game with `hasMultipliers` and no editor of its own is given the generic
 * name-to-multiplier paytable editor, whose save stores rows this game never
 * reads and never reaches either setting.
 */
export async function renderMinesChestMultipliersPage(
  root: HTMLElement,
  deps: MinesChestMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;
  const rtpRange = `${mineschestBpsToPct(MINESCHEST_MIN_RTP_BPS)}–${mineschestBpsToPct(MINESCHEST_MAX_RTP_BPS)}`;
  const rtpStepPct = mineschestBpsToPct(MINESCHEST_RTP_STEP_BPS);

  function sourceCell(key: MinesChestSettingKey, defaultText: string): string {
    return isIntegratorScope
      ? `<td data-tag="${key}" class="muted" style="min-width:150px">—</td>`
      : `<td class="muted">${defaultText}</td>`;
  }

  root.innerHTML = `
    <h1 class="page-title">Payout settings — Mines Chest</h1>
    <p class="page-sub">Mines Chest has two settings and no paytable to type. The <strong>RTP</strong> prices every win: a bet on a number of chests returns the RTP divided by its chance of winning, cut to two decimals, so no bet returns more than the RTP. <strong>Most chests per bet</strong> decides which of the ten bets the table offers. Neither setting changes which chests are explosive. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Settings</h2>
      <table>
        <thead><tr><th>Setting</th><th>Value</th><th>Reads as</th><th>${isIntegratorScope ? 'Stored now' : ''}</th></tr></thead>
        <tbody>
          <tr data-row="rtpBps">
            <td><strong>RTP (%)</strong><br><span class="muted">The most any bet returns · ${rtpRange} in steps of ${rtpStepPct}</span></td>
            <td><input id="mc-rtp" type="number" min="${mineschestBpsToPct(MINESCHEST_MIN_RTP_BPS)}" max="${mineschestBpsToPct(MINESCHEST_MAX_RTP_BPS)}" step="${rtpStepPct}" style="width:120px" /></td>
            <td class="muted" id="mc-rtp-hint">—</td>
            ${sourceCell('rtpBps', `default ${mineschestBpsToPct(MINESCHEST_DEFAULT_RTP_BPS)}%`)}
          </tr>
          <tr data-row="maxPicks">
            <td><strong>Most chests per bet</strong><br><span class="muted">A whole number · ${MINESCHEST_MIN_PICKS}–${MINESCHEST_PICKS_CEILING}</span></td>
            <td><input id="mc-picks" type="number" min="${MINESCHEST_MIN_PICKS}" max="${MINESCHEST_PICKS_CEILING}" step="1" style="width:120px" /></td>
            <td class="muted" id="mc-picks-hint">—</td>
            ${sourceCell('maxPicks', `default ${MINESCHEST_DEFAULT_MAX_PICKS}`)}
          </tr>
        </tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="mc-reset" class="ghost">Reset to default</button>
        ${isIntegratorScope ? '<button id="mc-reset-all" class="ghost">Reset all to provider default</button>' : ''}
        <button id="mc-save" class="primary">Propose change</button>
        <span id="mc-msg" class="muted"></span>
      </div>
      ${
        isIntegratorScope
          ? '<p class="muted" style="margin:10px 0 0;font-size:12px">A proposal always stores both settings for this integrator, the one you did not change at the value shown. Once approved, neither follows a later change to the provider default.</p>'
          : ''
      }
    </div>

    <div class="card">
      <h2>What these settings produce</h2>
      <div id="mc-preview"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Largest possible win</h2>
      <div id="mc-largest"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="mc-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rtpEl = root.querySelector<HTMLInputElement>('#mc-rtp')!;
  const picksEl = root.querySelector<HTMLInputElement>('#mc-picks')!;
  const rtpHintEl = root.querySelector<HTMLElement>('#mc-rtp-hint')!;
  const picksHintEl = root.querySelector<HTMLElement>('#mc-picks-hint')!;
  const previewEl = root.querySelector<HTMLElement>('#mc-preview')!;
  const largestEl = root.querySelector<HTMLElement>('#mc-largest')!;
  const msgEl = root.querySelector<HTMLElement>('#mc-msg')!;
  const inputs: Record<MinesChestSettingKey, HTMLInputElement> = {
    rtpBps: rtpEl,
    maxPicks: picksEl,
  };

  // What each setting falls back to beneath this scope (the provider default,
  // then the baked one), for "Reset all to provider default".
  const inherited: Record<MinesChestSettingKey, number> = {
    rtpBps: MINESCHEST_DEFAULT_RTP_BPS,
    maxPicks: MINESCHEST_DEFAULT_MAX_PICKS,
  };
  // The table's maximum bet per currency, read with the settings.
  let maxBets: MinesChestMaxBet[] | null = null;

  function shown(key: MinesChestSettingKey, value: number): string {
    return key === 'rtpBps' ? mineschestBpsToPct(value) : String(value);
  }

  /** Say where a setting's STORED value comes from (integrator scope only).
   *  It describes what is saved today, not what is typed: a proposal always
   *  carries both settings. */
  function markStored(key: MinesChestSettingKey, setting: MinesChestSetting): void {
    const tagEl = root.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    if (!tagEl) return;
    const isOverride = setting.source === 'override';
    const beneath = shown(key, setting.inherited) + (key === 'rtpBps' ? '%' : '');
    tagEl.innerHTML = isOverride
      ? `<strong style="color:var(--accent)">override</strong><br>provider default ${beneath}`
      : `inherited<br>provider default ${beneath}`;
  }

  /** Redraw everything derived from the two inputs. A pair that could not be
   *  saved gets its reason in place of a table priced on a figure the game
   *  would never run. */
  function refreshDerived(): void {
    const rtpBps = mineschestPctToBps(rtpEl.value);
    const maxPicks = mineschestParsePicks(picksEl.value);
    const errors = mineschestProposalErrors({ version: 1, rtpBps, maxPicks });
    const rtpOk = !errors.includes(MINESCHEST_RTP_RULE);
    const picksOk = !errors.includes(MINESCHEST_PICKS_RULE);

    const rtp = rtpBps / 10_000;
    rtpHintEl.innerHTML = rtpOk
      ? `House edge <strong style="color:${rtpColor(rtp)}">${((1 - rtp) * 100).toFixed(2)}%</strong>`
      : `<span class="error">${MINESCHEST_RTP_RULE}</span>`;
    picksHintEl.innerHTML = picksOk
      ? maxPicks === MINESCHEST_MIN_PICKS
        ? 'Bets on 1 chest only'
        : `Bets on ${MINESCHEST_MIN_PICKS} to ${maxPicks} chests`
      : `<span class="error">${MINESCHEST_PICKS_RULE}</span>`;

    if (errors.length > 0) {
      const why = `<p class="error" style="margin:0">${errors.join(' ')}</p>`;
      previewEl.innerHTML = why;
      largestEl.innerHTML = why;
      return;
    }
    previewEl.innerHTML = mineschestPayoutTableHtml(rtpBps, maxPicks);
    largestEl.innerHTML = mineschestLargestWinHtml(rtpBps, maxPicks, maxBets);
  }

  /** Load both inputs with the values in force, so the setting the admin does
   *  not touch is proposed at its current value. */
  function fillForm(settings: Record<MinesChestSettingKey, MinesChestSetting>): void {
    const form = mineschestFormValues(settings);
    rtpEl.value = form.rtpPct;
    picksEl.value = form.maxPicks;
    for (const key of ['rtpBps', 'maxPicks'] as const) {
      inherited[key] = settings[key].inherited;
      markStored(key, settings[key]);
    }
    refreshDerived();
  }

  /** The propose payload: both settings, in either scope. */
  function readForm(): MinesChestMultipliersOverride {
    return mineschestProposal(rtpEl.value, picksEl.value);
  }

  for (const key of ['rtpBps', 'maxPicks'] as const) {
    inputs[key].addEventListener('input', refreshDerived);
  }

  /** Read the settings in force and the table limits, respecting the scope.
   *  One layered read serves both: this scope's own values, and beneath them
   *  the provider's when this is an integrator. */
  async function reload(): Promise<void> {
    const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
    const provider = isIntegratorScope ? inh.providerDefault : null;
    maxBets = mineschestMaxBets(deps.gameId, inh.ownOverride, provider);
    fillForm(mineschestResolveSettings(inh.ownOverride.multipliers, provider?.multipliers));
  }

  try {
    await reload();
  } catch (err) {
    const why = `<p class="error" style="margin:0">Could not load the current settings: ${escapeHtml(describeError(err))}</p>`;
    previewEl.innerHTML = why;
    largestEl.innerHTML = why;
  }

  root.querySelector<HTMLButtonElement>('#mc-reset')!.addEventListener('click', () => {
    rtpEl.value = mineschestBpsToPct(MINESCHEST_DEFAULT_RTP_BPS);
    picksEl.value = String(MINESCHEST_DEFAULT_MAX_PICKS);
    refreshDerived();
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#mc-reset-all')?.addEventListener('click', () => {
    for (const key of ['rtpBps', 'maxPicks'] as const) {
      inputs[key].value = shown(key, inherited[key]);
    }
    refreshDerived();
    msgEl.textContent = 'Both settings set to the provider default (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#mc-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const proposal = readForm();
    const errors = mineschestProposalErrors(proposal);
    if (errors.length > 0) {
      msgEl.textContent = errors.join(' ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        mineschestMultipliersSchema.parse(proposal),
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = describeError(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#mc-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Proposed settings</th><th></th></tr></thead>
      <tbody>${subset
        .map((a) => {
          const pending = a.pendingConfig as { rtpBps?: unknown; maxPicks?: unknown } | null;
          const parts: string[] = [];
          if (typeof pending?.rtpBps === 'number') {
            parts.push(`RTP ${mineschestBpsToPct(pending.rtpBps)}%`);
          }
          if (typeof pending?.maxPicks === 'number') {
            parts.push(`most chests per bet ${pending.maxPicks}`);
          }
          return `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>${parts.length > 0 ? parts.join(' · ') : '—'}</td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`;
        })
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved. New rounds use the new settings.';
          msgEl.className = 'muted';
          await refreshPending();
          await reload();
        } catch (err) {
          showToast(describeError(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(describeError(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}

function describeError(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}
